import {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';

import {loadBoxData} from '../../state/media/actions';
import {type MediaPanelCardControl} from '../MediaCard';
import {loadPointCloudScene} from './parsePointCloudScene';
import {
  applyFilter,
  getFilterFromBBoxConfig,
  getFilteringOptionsForPointCloud,
} from './pointCloudFiltering';
import {type PointCloudRgb, type PointCloudScene} from './types';

type UsePointCloudSceneArgs = {
  blob: Blob;
  mediaKey: string;
  mediaPanelRefId: string;
  controls: MediaPanelCardControl | undefined;
  pointColor?: PointCloudRgb;
};

type PointCloudSceneState = {
  scene: PointCloudScene | undefined;
  sourcePointCount: number;
  loadErrorMessage: string | undefined;
};

function withPointColor(
  scene: PointCloudScene,
  pointColor: PointCloudRgb | undefined
): PointCloudScene {
  if (!pointColor) {
    return scene;
  }
  return {
    ...scene,
    points: scene.points.map(point => ({
      ...point,
      color: pointColor,
    })),
  };
}

/**
 * Parse Object3D JSON into the shared scene, truncate once, apply panel
 * pointColor and bbox filters. Babylon conversion stays in the renderer.
 */
export function usePointCloudScene({
  blob,
  mediaKey,
  mediaPanelRefId,
  controls,
  pointColor,
}: UsePointCloudSceneArgs): PointCloudSceneState {
  const dispatch = useDispatch();
  const [scene, setScene] = useState<PointCloudScene>();
  const [sourcePointCount, setSourcePointCount] = useState(0);
  const [rawScene, setRawScene] = useState<PointCloudScene>();
  const [rawSourcePointCount, setRawSourcePointCount] = useState(0);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string>();

  useEffect(() => {
    // Empty placeholder blobs are used while the media panel download is still
    // in flight; parsing them yields "Unexpected end of JSON input".
    // We should not typically hit here. The component should not reload until the blob is loaded.
    if (blob.size === 0) {
      setRawScene(undefined);
      setRawSourcePointCount(0);
      setLoadErrorMessage(undefined);
      return;
    }

    let cancelled = false;
    // Clear any stale error from a previous blob
    setLoadErrorMessage(undefined);
    new Response(blob)
      .text()
      .then(t => {
        if (cancelled) {
          return;
        }
        try {
          const {scene: parsed, sourcePointCount: count} =
            loadPointCloudScene(t);
          setRawScene(parsed);
          setRawSourcePointCount(count);
          setLoadErrorMessage(undefined);
        } catch (e) {
          if (e instanceof Error) {
            setLoadErrorMessage(e.message);
          } else {
            setLoadErrorMessage('unknown error');
            console.error('unknown render error:', e);
          }
        }
      })
      .catch(e => {
        if (!cancelled) {
          setLoadErrorMessage(e instanceof Error ? e.message : String(e));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [blob]);

  useEffect(() => {
    if (!rawScene) {
      return;
    }

    const colored = withPointColor(rawScene, pointColor);
    const {boxData, newClassIdToLabel} =
      getFilteringOptionsForPointCloud(colored);
    const filtered = applyFilter(
      colored,
      getFilterFromBBoxConfig(controls?.boundingBoxControl, newClassIdToLabel)
    );

    setScene(filtered);
    setSourcePointCount(rawSourcePointCount);

    dispatch(
      loadBoxData({
        mediaKey,
        panelID: mediaPanelRefId,
        mediaID: '1',
        boxData,
      })
    );
  }, [
    rawScene,
    rawSourcePointCount,
    dispatch,
    mediaKey,
    mediaPanelRefId,
    controls?.boundingBoxControl,
    pointColor,
  ]);

  return {scene, sourcePointCount, loadErrorMessage};
}
